// components/market/CategoryChips.tsx
//
// Sélecteur de catégorie sous forme de chips horizontaux scrollables — pas
// de dépendance externe (pas de @react-native-picker/picker). Charge la
// liste via useCategories() (voir hooks/market/useMarketCategories.ts,
// action backend get-categories).
//
// Deux usages couverts par le même composant :
//   1. Filtre optionnel dans market/index.tsx (recherche de produits) :
//      allowClear=true, avec un chip "Toutes" en tête qui remet la
//      sélection à vide (undefined).
//   2. Champ obligatoire dans gerer-produit/[productId].tsx (catégorie
//      d'un produit) : allowClear=false, pas de chip "Toutes" — le
//      produit doit toujours avoir une catégorie (contrainte NOT NULL en
//      base, voir market_products.category_id).
//
// value/onValueChange fonctionnent comme un champ contrôlé classique :
// value est le category_id sélectionné (ou undefined si allowClear et
// rien de choisi), onValueChange reçoit le nouveau category_id (ou
// undefined si le chip "Toutes" est tapé).
//
// PATCH i18n : les textes d'état (chargement, erreur, chip "Toutes")
// passent par react-i18next (namespace market.components.categoryChips).
// category.name et category.icon_name viennent du backend et restent
// non traduits, comme pour toutes les données saisies par les vendeurs.

import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';

import { useCategories } from '@/hooks/market/useMarketCategories';

const NATIVE = Platform.OS !== 'web';
const BRAND = '#6610B1';
const BRAND_SOFT = '#F3E8FF';
const BORDER = '#E5D9FF';
const INK = '#333';
const MUTED = '#8A8496';

interface CategoryChipsProps {
  value?: string;
  onValueChange: (categoryId: string | undefined) => void;
  allowClear?: boolean;
  label?: string;
}

export default function CategoryChips({
  value,
  onValueChange,
  allowClear = false,
  label,
}: CategoryChipsProps) {
  const { t } = useTranslation();
  const { categories, loading, error } = useCategories();

  const handlePress = (categoryId: string | undefined) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onValueChange(categoryId);
  };

  if (loading) {
    return (
      <View style={styles.loadingRow}>
        <ActivityIndicator size="small" color={BRAND} />
        <Text style={styles.loadingText}>{t('market.components.categoryChips.loading')}</Text>
      </View>
    );
  }

  if (error || categories.length === 0) {
    return (
      <Text style={styles.errorText}>
        {t('market.components.categoryChips.error')}
      </Text>
    );
  }

  return (
    <View>
      {!!label && <Text style={styles.label}>{label}</Text>}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
      >
        {allowClear && (
          <TouchableOpacity
            style={[styles.chip, value === undefined && styles.chipActive]}
            activeOpacity={0.8}
            onPress={() => handlePress(undefined)}
          >
            <Text style={[styles.chipText, value === undefined && styles.chipTextActive]}>
              {t('market.components.categoryChips.allLabel')}
            </Text>
          </TouchableOpacity>
        )}
        {categories.map((category) => {
          const isActive = value === category.id;
          return (
            <TouchableOpacity
              key={category.id}
              style={[styles.chip, isActive && styles.chipActive]}
              activeOpacity={0.8}
              onPress={() => handlePress(category.id)}
            >
              {!!category.icon_name && (
                <Ionicons
                  name={category.icon_name as any}
                  size={14}
                  color={isActive ? '#fff' : BRAND}
                  style={styles.chipIcon}
                />
              )}
              <Text style={[styles.chipText, isActive && styles.chipTextActive]}>
                {category.name}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: INK,
    marginBottom: 8,
  },
  row: {
    gap: 8,
    paddingVertical: 2,
    paddingRight: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: BRAND_SOFT,
    borderWidth: 1,
    borderColor: BORDER,
  },
  chipActive: {
    backgroundColor: BRAND,
    borderColor: BRAND,
  },
  chipIcon: {
    marginRight: 5,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND,
  },
  chipTextActive: {
    color: '#fff',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
  loadingText: {
    fontSize: 13,
    color: MUTED,
  },
  errorText: {
    fontSize: 13,
    color: MUTED,
    paddingVertical: 8,
  },
});
